import { ChevronLeft, ChevronRight, RefreshCw, X } from "lucide-react";
import { useId, useState } from "react";
import { useSearchParams } from "react-router";

import { queryKeys } from "@/api/hooks";
import {
  classifyRunReadFailure,
  usePromptCallPageQuery,
} from "@/api/previewInsights";
import { QueryPendingState } from "@/api/QueryPendingState";
import { cn } from "@/lib/cn";
import { RUN_EVENT_PARAM, readEventRefParam } from "@/lib/previewDecisions";
import {
  CONTENT_UNAVAILABLE_LABELS,
  canStillChange,
  describeView,
  type PreviewRunView,
} from "@/lib/previewInsights";
import {
  EVENT_STATUS_LABELS,
  EVENT_STATUS_TONES,
  type PromptCall,
} from "@/lib/previewPromptCall";
import {
  NO_PROMPT_FILTER,
  PROMPT_FILTER_LABELS,
  describeListing,
  isFiltered,
  promptFilterAxes,
  promptFilterQuery,
  readPromptCallListing,
  readPromptFilter,
  withAxis,
  writePromptFilter,
  type PromptCallGroup,
  type PromptFilter,
  type PromptFilterAxisState,
} from "@/lib/previewPromptInspector";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { EmptyState, ErrorState } from "@/ui/states";

import { PromptCallCard } from "./PreviewRunPromptCall";
import { Identifier, PreviewReadFailureState } from "./previewRunParts";

/**
 * Preview Insights — the Prompt Inspector (#1100, approved design §10.3).
 *
 * The left pane lists every prompt call of a run, grouped by turn and agent
 * and narrowable by turn, agent, phase and call type. The right pane shows the
 * one call a clinician selected. #1099 built the detail; this leaf builds the
 * way to reach any of them.
 *
 * ## What the browser never does
 *
 * It never downloads the run. One request answers one bounded page — the
 * server applies the narrowing, cuts the page and counts every axis over the
 * whole run (`GET …/prompt-calls`) — so a four-hundred-call trace costs the
 * same one request a three-call trace costs, renders the same twenty-five
 * rows, and fetches no prompt text for any of them. A body is read only by
 * `PromptCallCard`, only for the selected call, and only when the clinician
 * presses its load control.
 *
 * It never filters, sorts or regroups either. Filtering would need the run;
 * sorting would need a comparator this module has no business owning. The
 * list is rendered in the order it arrived, and a group header appears where
 * the coordinate changes — so a group boundary can only fall where the
 * trace's own order puts one.
 *
 * ## The selection is an address, which is why it survives
 *
 * `?event=` names the selected call and `?turn=&agent=&phase=&callType=` name
 * the filter, so both are shareable and both survive a reload. Nothing about
 * them is positional: the detail read takes a REFERENCE, and the list's cursor
 * is the trace's own sequence. A poll that appends three newly captured calls
 * therefore moves nothing — the selected call stays selected, the page keeps
 * its boundaries, and the new calls appear where the conversation put them.
 *
 * A selection outside the current filter is KEPT and said so, rather than
 * cleared: the clinician chose it, it is still a call of this run, and
 * narrowing the list is not a reason to close what they were reading.
 *
 * ## Absence is a state, never a blank pane (ADR 0013 N7)
 *
 * A run that cannot be read, a run that has recorded no prompt call yet, a
 * run that recorded none at all, a filter that matches none, and a filter
 * naming a value this run never records are five different sentences below,
 * and each names what it knows and what it does not.
 */
export function PreviewRunPromptInspector({
  projectId,
  previewRunRef,
  view,
  shown,
  paused,
  pollIntervalMs,
}: {
  projectId: string;
  previewRunRef: string;
  /** The run page's own view of the envelope; decides whether anything can be listed. */
  view: PreviewRunView;
  /**
   * Whether the Prompt Inspector tab has been shown at least once. The tab
   * strip latches it and never clears it: nothing is read before the tab is
   * first shown, and from then on the panel stays mounted behind the other
   * tabs, so a selected call and a loaded prompt survive a look elsewhere.
   */
  shown: boolean;
  /** A clinician-held pause on the run page. Stops this list's timer too. */
  paused: boolean;
  pollIntervalMs: number;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = readPromptFilter(searchParams);

  // THE SELECTION IS THE ADDRESS'S WHENEVER THE ADDRESS NAMES ONE — a shared
  // link, a reload, an evidence link from another tab — and this panel's own
  // otherwise. The two are needed together because the run page clears
  // `?event=` when the clinician moves between tabs (a reference named for one
  // tab is not a reference for another), while this panel stays MOUNTED behind
  // them: without the local half, a look at Flow & Decisions would close the
  // call a clinician was reading and discard the body they had already asked
  // for, which is the defect #1121's review round 4 found on the same panel.
  const urlEvent = readEventRefParam(searchParams.get(RUN_EVENT_PARAM));
  const [picked, setPicked] = useState<string | null>(urlEvent);
  const selected = urlEvent ?? picked;

  // THE CURSOR IS NOT IN THE ADDRESS. A page position is not a fact about the
  // run — it is where this clinician happens to be looking — and putting it
  // in a shared link would hand someone else a page rather than a run.
  //
  // It is held BESIDE the filter it belongs to and read back only when the
  // two still agree, so narrowing returns to the first page without an effect
  // and without a render in between: an effect would have issued one request
  // for the new filter at the old cursor before resetting it, which is a
  // wasted round trip and a flash of the wrong page.
  const filterKey = JSON.stringify(filter);
  const [paging, setPaging] = useState<{
    filter: string;
    cursor: number | null;
  }>({
    filter: filterKey,
    cursor: null,
  });
  const cursor = paging.filter === filterKey ? paging.cursor : null;
  const setCursor = (next: number | null) =>
    setPaging({ filter: filterKey, cursor: next });

  const readable =
    view.kind === "live" ||
    view.kind === "completed" ||
    view.kind === "incomplete";
  const page = usePromptCallPageQuery(
    projectId,
    previewRunRef,
    {
      ...promptFilterQuery(filter),
      ...(cursor === null ? {} : { afterSequence: cursor }),
    },
    {
      enabled: shown && readable,
      // Polled only while the run can still change, and never while the
      // clinician holds the pause. A completed run's list cannot grow, so
      // re-reading it would spend requests to learn nothing.
      pollIntervalMs: !paused && canStillChange(view) ? pollIntervalMs : false,
    },
  );

  function select(eventRef: string | null) {
    setPicked(eventRef);
    setSearchParams(
      (current: URLSearchParams) => {
        const params = new URLSearchParams(current);
        if (eventRef === null) {
          params.delete(RUN_EVENT_PARAM);
        } else {
          params.set(RUN_EVENT_PARAM, eventRef);
        }
        return params;
      },
      { replace: true },
    );
  }

  function narrow(next: PromptFilter) {
    setSearchParams(
      (current: URLSearchParams) =>
        writePromptFilter(new URLSearchParams(current), next),
      { replace: true },
    );
  }

  if (!shown) {
    return null;
  }

  if (view.kind === "waiting") {
    return (
      <EmptyState
        title="No prompt call has been recorded yet"
        description={`${describeView(view)} Prompt calls appear here once Liv makes its first model call.`}
      />
    );
  }
  if (view.kind === "unavailable") {
    return (
      <EmptyState
        title="The Prompt Inspector cannot read this run"
        description={`${describeView(view)} No prompt call can be listed while the trace is unavailable.`}
      />
    );
  }

  // NOTHING FROM A PREVIOUS VISIT IS SHOWN AS THIS ONE'S ANSWER — the same
  // `isFetchedAfterMount` gate the run page applies to the envelope.
  if (page.isPending || !page.isFetchedAfterMount) {
    return (
      <QueryPendingState
        isPaused={page.isPaused}
        failureReason={page.failureReason}
        label="Listing this run's prompt calls"
        errorTitle="This run's prompt calls could not be listed"
        rows={4}
        queryKey={queryKeys.previewRunPromptCalls(projectId, previewRunRef, {})}
      />
    );
  }

  if (page.isError) {
    return (
      <PreviewReadFailureState
        subject="prompt_call"
        failure={classifyRunReadFailure(page.error)}
        error={page.error}
        onRetry={() => void page.refetch()}
      />
    );
  }

  const listing = readPromptCallListing(page.data);
  if (listing.kind === "content_unavailable") {
    const reason = page.data.run.contentUnavailableReason ?? null;
    return (
      <EmptyState
        title="This run's trace has no readable content"
        description={
          reason
            ? `The Editor Service reports that ${CONTENT_UNAVAILABLE_LABELS[reason]}. No prompt call can be listed.`
            : "The Editor Service reports no readable content for this run and names no reason. No prompt call can be listed."
        }
      />
    );
  }
  if (listing.kind === "unreadable") {
    return (
      <ErrorState
        title="This run's prompt calls are not ones this build can read"
        description={`The Editor Service relayed a call this build cannot place (at ${listing.at}). Nothing is listed rather than part of the run.`}
      />
    );
  }

  const axes = promptFilterAxes(listing.facets, listing.filterSupport, filter);
  const onPage =
    listing.calls.find((call) => call.eventRef === selected) ?? null;

  return (
    <div className="space-y-4">
      <PromptFilters
        axes={axes}
        filter={filter}
        onNarrow={narrow}
        onRefresh={() => void page.refetch()}
        refreshing={page.isFetching}
        // The run can still change, so the list is on a timer. Saying so is
        // what makes a row that appears on its own explicable.
        live={!paused && canStillChange(view)}
      />

      <p role="status" className="text-sm text-muted-foreground">
        {describeListing(listing, filter)}
        {listing.total > 0 && view.kind === "incomplete"
          ? " This run's capture is incomplete, so calls may be missing from it."
          : ""}
      </p>

      {/*
        Two panes, two landmarks. The list and the detail are separate regions
        of one screen, and a screen-reader user moving between them needs to be
        told which one they have reached — "back to the list" is a real
        navigation on this page, not a scroll.
      */}
      <div className="grid gap-4 lg:grid-cols-[minmax(16rem,22rem)_1fr]">
        <section aria-label="Prompt call list">
          <PromptCallList
            groups={listing.groups}
            selected={selected}
            onSelect={select}
            emptyBecause={
              listing.total === 0
                ? canStillChange(view)
                  ? "The trace is readable and holds no prompt call so far. The run can still change; check again after the next turn."
                  : "The trace is readable and holds no prompt call. The run can no longer change on its own."
                : "No prompt call in this run matches the filter above. Clear a filter to widen the list."
            }
            page={{
              hasMore: listing.hasMore,
              atStart: cursor === null,
              onNext:
                listing.nextAfterSequence === null
                  ? null
                  : () => setCursor(listing.nextAfterSequence),
              // BACK TO THE START, never "the previous page": this read is cut
              // on a forward cursor, and inventing a backward one from a page
              // already shown would be a position rather than a fact. A run
              // this deep is reached by narrowing, which is what the filters
              // are for.
              onStart: () => setCursor(null),
            }}
          />
        </section>

        <section aria-label="Prompt call detail">
          {selected === null ? (
            <EmptyState
              title="No prompt call selected"
              description="Choose a call from the list to see its exact effective prompt, the messages sent, the model result and the runtime identity. Nothing is fetched until you do."
            />
          ) : (
            <SelectedPromptCall
              key={selected}
              eventRef={selected}
              call={onPage}
              projectId={projectId}
              previewRunRef={previewRunRef}
              onClear={() => select(null)}
              filtered={isFiltered(filter)}
            />
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * The detail pane's frame.
 *
 * The card is rendered from the COMPACT call on the current page when the
 * list holds it. When the list does not — the clinician narrowed the filter,
 * or paged past it — the selection is still a real call of this run and is
 * still readable by reference; what is missing is the compact record this
 * pane would have shown beside it, so the pane says that rather than
 * pretending the call is gone or silently selecting another.
 */
function SelectedPromptCall({
  eventRef,
  call,
  projectId,
  previewRunRef,
  onClear,
  filtered,
}: {
  eventRef: string;
  call: PromptCall | null;
  projectId: string;
  previewRunRef: string;
  onClear: () => void;
  filtered: boolean;
}) {
  if (call !== null) {
    return (
      <PromptCallCard
        call={call}
        projectId={projectId}
        previewRunRef={previewRunRef}
        title="Selected prompt call"
      />
    );
  }
  return (
    <Card>
      <CardHeader className="flex flex-wrap items-start justify-between gap-3">
        <CardTitle>Selected prompt call</CardTitle>
        <Button variant="outline" size="sm" onClick={onClear}>
          <X aria-hidden="true" className="h-4 w-4" />
          Clear selection
        </Button>
      </CardHeader>
      <CardBody className="space-y-3 text-sm text-muted-foreground">
        <p>
          The call you selected is not on the list as it is currently shown
          {filtered ? ", because the filter above excludes it" : ""}. It has not
          been cleared and nothing about it has been read.
        </p>
        <p>
          <Identifier value={eventRef} />
        </p>
        <p>
          {filtered
            ? "Clear the filter to bring it back into the list, or clear the selection."
            : "Go back to the start of the list to bring it back, or clear the selection."}
        </p>
      </CardBody>
    </Card>
  );
}

/** The four narrowing controls, the live/refresh state, and the clear-all. */
function PromptFilters({
  axes,
  filter,
  onNarrow,
  onRefresh,
  refreshing,
  live,
}: {
  axes: readonly PromptFilterAxisState[];
  filter: PromptFilter;
  onNarrow: (filter: PromptFilter) => void;
  onRefresh: () => void;
  refreshing: boolean;
  live: boolean;
}) {
  const groupId = useId();
  return (
    <section aria-labelledby={groupId} className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 id={groupId} className="text-sm font-semibold text-foreground">
          Filter prompt calls
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          {live ? (
            <Badge tone="accent">
              Live — new calls appear as they are captured
            </Badge>
          ) : null}
          {isFiltered(filter) ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onNarrow(NO_PROMPT_FILTER)}
            >
              <X aria-hidden="true" className="h-4 w-4" />
              Clear filters
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
          >
            <RefreshCw aria-hidden="true" className="h-4 w-4" />
            Refresh list
          </Button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {axes.map((axis) => (
          <FilterSelect
            key={axis.axis}
            state={axis}
            onChange={(value) => onNarrow(withAxis(filter, axis.axis, value))}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * One axis.
 *
 * A native `<select>`, which is the control that is already keyboard
 * operable, already announced with its label and its value, and already
 * usable on a phone — and whose options carry their counts in the option text
 * so a screen reader reads "guard, 3 calls" rather than a number floating
 * beside a name.
 *
 * A value the run does not record cannot be an option, so it is stated
 * underneath instead: an option list can say "these exist", and only a
 * sentence can say "the one you asked for does not".
 */
function FilterSelect({
  state,
  onChange,
}: {
  state: PromptFilterAxisState;
  onChange: (value: string | null) => void;
}) {
  const id = useId();
  const noteId = `${id}-note`;
  const axisName = PROMPT_FILTER_LABELS[state.axis].toLowerCase();
  const notes: string[] = [];
  if (state.unsupported) {
    // The run records NOTHING under this value, whatever else is filtered —
    // the server said so; it is not inferred from the options below, which are
    // counted with the other filters applied.
    notes.push(
      `This run records no prompt call with ${axisName} “${state.active}”, so no call can match it. Clear this filter.`,
    );
  }
  if (state.excludedByOtherFilters) {
    // A different fact with a different remedy: the run DOES record this
    // value, and the other filters exclude every call that carries it.
    notes.push(
      `This run records prompt calls with ${axisName} “${state.active}”, but the other filters exclude all of them. Widen one of the others to see them.`,
    );
  }
  if (state.unreported > 0) {
    notes.push(
      `${state.unreported} ${state.unreported === 1 ? "call reports" : "calls report"} no ${axisName}; no filter on this axis can list ${state.unreported === 1 ? "it" : "them"}.`,
    );
  }
  if (
    state.options.length === 0 &&
    !state.unsupported &&
    !state.excludedByOtherFilters
  ) {
    notes.push("No value on this axis is recorded by the calls listed.");
  }
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-foreground">
        {PROMPT_FILTER_LABELS[state.axis]}
      </label>
      <select
        id={id}
        // Direction is the CHROME's: these are machine identifiers, and the
        // control around them belongs to the page, not to the run.
        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        value={state.active ?? ""}
        aria-describedby={notes.length > 0 ? noteId : undefined}
        aria-invalid={state.unsupported || undefined}
        onChange={(event) => onChange(event.currentTarget.value || null)}
      >
        <option value="">All</option>
        {(state.unsupported || state.excludedByOtherFilters) &&
        state.active !== null ? (
          // The address's own value, kept selectable so the control shows what
          // is actually applied. Dropping it would have made the control say
          // "All" while the list was narrowed to nothing — and the two states
          // read differently because they ARE different: one value this run
          // never records, one it records under other filters.
          <option value={state.active}>
            {state.active}
            {state.unsupported ? " — not in this run" : " — none under the other filters"}
          </option>
        ) : null}
        {state.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label} ({option.count})
          </option>
        ))}
      </select>
      {notes.length > 0 ? (
        <p id={noteId} className="text-xs text-muted-foreground">
          {notes.join(" ")}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The list itself: groups in the order the server listed them, and inside
 * each, calls in the order the conversation made them.
 *
 * A real listbox. Up/Down move the selection, Home/End jump to the ends, and
 * the roving tab index keeps the whole list one tab stop — the pattern the
 * tab strip above it already uses. Vertical keys are used rather than
 * horizontal ones precisely because the list is vertical in both writing
 * directions: a right-to-left page mirrors the layout, not the axis.
 */
function PromptCallList({
  groups,
  selected,
  onSelect,
  emptyBecause,
  page,
}: {
  groups: readonly PromptCallGroup[];
  selected: string | null;
  onSelect: (eventRef: string) => void;
  emptyBecause: string;
  page: {
    hasMore: boolean;
    atStart: boolean;
    onNext: (() => void) | null;
    onStart: () => void;
  };
}) {
  const listId = useId();
  const calls = groups.flatMap((group) => group.calls);
  // The roving stop is the selection when it is on this page, and otherwise
  // the first row: a list whose selection is elsewhere must still be
  // reachable with one Tab.
  const focusable =
    calls.find((call) => call.eventRef === selected)?.eventRef ??
    calls[0]?.eventRef ??
    null;

  function move(from: string, step: number | "start" | "end") {
    const index = calls.findIndex((call) => call.eventRef === from);
    const next =
      step === "start"
        ? 0
        : step === "end"
          ? calls.length - 1
          : Math.min(Math.max(index + step, 0), calls.length - 1);
    const target = calls[next];
    if (target) {
      onSelect(target.eventRef);
      document.getElementById(`${listId}-${target.eventRef}`)?.focus();
    }
  }

  if (calls.length === 0) {
    return (
      <EmptyState title="No prompt call to list" description={emptyBecause} />
    );
  }

  return (
    <div className="space-y-3">
      <div role="listbox" aria-label="Prompt calls" className="space-y-3">
        {groups.map((group) => (
          <div key={group.key} role="group" aria-label={groupLabel(group)}>
            <p className="px-1 pb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              {groupLabel(group)}
            </p>
            {/*
              The options are children of the group, with no list between
              them: `listbox` owns `group` owns `option`, and a `ul`/`li` pair
              in the middle would put `list`/`listitem` roles where ARIA
              admits none.
            */}
            <div className="space-y-1">
              {group.calls.map((call) => (
                <button
                  key={call.eventRef}
                  type="button"
                  role="option"
                  id={`${listId}-${call.eventRef}`}
                  aria-selected={call.eventRef === selected}
                  tabIndex={call.eventRef === focusable ? 0 : -1}
                  onClick={() => onSelect(call.eventRef)}
                  onKeyDown={(event) => {
                    const step =
                      event.key === "ArrowDown"
                        ? 1
                        : event.key === "ArrowUp"
                          ? -1
                          : event.key === "Home"
                            ? ("start" as const)
                            : event.key === "End"
                              ? ("end" as const)
                              : null;
                    if (step === null) {
                      return;
                    }
                    event.preventDefault();
                    move(call.eventRef, step);
                  }}
                  className={cn(
                    "block w-full rounded-md border px-3 py-2 text-start text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                    call.eventRef === selected
                      ? "border-primary bg-primary/5 text-foreground"
                      : "border-border text-foreground hover:bg-muted/50",
                  )}
                >
                  <span className="flex flex-wrap items-center gap-2">
                    {call.callType !== null ? (
                      <Identifier value={call.callType} />
                    ) : (
                      <span className="text-muted-foreground">
                        Call type not reported
                      </span>
                    )}
                    <Badge tone={EVENT_STATUS_TONES[call.status]}>
                      {EVENT_STATUS_LABELS[call.status]}
                    </Badge>
                  </span>
                  {/* The producer's own label: clinical text, so it chooses its own direction. */}
                  <span
                    dir="auto"
                    className="mt-0.5 block text-muted-foreground"
                  >
                    {call.label ?? "Label not reported"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {page.hasMore || !page.atStart ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={page.onStart}
            disabled={page.atStart}
          >
            <ChevronLeft
              aria-hidden="true"
              className="h-4 w-4 rtl:rotate-180"
            />
            Back to the first calls
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => page.onNext?.()}
            disabled={page.onNext === null}
          >
            More calls
            <ChevronRight
              aria-hidden="true"
              className="h-4 w-4 rtl:rotate-180"
            />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function groupLabel(group: PromptCallGroup): string {
  const turn =
    group.turnNumber === null
      ? "Turn not reported"
      : `Turn ${group.turnNumber}`;
  return group.agent === null
    ? `${turn} — agent not reported`
    : `${turn} — ${group.agent}`;
}
